import { lazy, Suspense, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";

const ZLDCalculatorSheet = lazy(() => import("./ZLDCalculatorSheet"));

interface ZLDCalculatorTriggerProps {
  className?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ZLDCalculatorTrigger = ({ className, isOpen: externalIsOpen, onOpenChange }: ZLDCalculatorTriggerProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Use external control if provided, otherwise internal
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  // Sync hasInteracted when externally opened
  useEffect(() => {
    if (externalIsOpen && !hasInteracted) {
      setHasInteracted(true);
    }
  }, [externalIsOpen, hasInteracted]);

  const handleOpen = () => {
    setHasInteracted(true);
    if (isControlled) {
      onOpenChange?.(true);
    } else {
      setInternalIsOpen(true);
    }
  };

  const handleClose = () => {
    if (isControlled) {
      onOpenChange?.(false);
    } else {
      setInternalIsOpen(false);
    }
  };

  // If externally controlled without a button, just render the sheet
  if (isControlled) {
    return hasInteracted ? (
      <Suspense fallback={null}>
        <ZLDCalculatorSheet isOpen={isOpen} onClose={handleClose} />
      </Suspense>
    ) : null;
  }

  return (
    <>
      <Button
        variant="cta"
        size="xl"
        onClick={handleOpen}
        aria-label="Get Instant ZLD Plant Recommendation"
        className={`shadow-medium hover:shadow-elevated font-bold tracking-tight max-w-full whitespace-normal text-center leading-snug ${className}`}
      >
        <Calculator className="w-5 h-5 mr-2 flex-shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">Get Instant ZLD Plant Recommendation</span>
        <span className="sm:hidden">Get ZLD Recommendation</span>
      </Button>

      {hasInteracted && (
        <Suspense fallback={null}>
          <ZLDCalculatorSheet isOpen={isOpen} onClose={handleClose} />
        </Suspense>
      )}
    </>
  );
};

export default ZLDCalculatorTrigger;
